import React from 'react';
import { makeStyles } from '@material-ui/styles';
import { Box, Container, Divider, Typography, Button } from '@material-ui/core';
import { Helmet } from 'react-helmet';
import { Link as RouterLink } from 'react-router-dom';
import usePageTitle from '../../hooks/usePageTitle';
import { APP_PATH_PRICING, APP_PATH_SIGNUP } from '../../config';

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(12),
    background: theme.palette.background.default
  },
  introSection: {
    textAlign: 'center',
    marginBottom: theme.spacing(10)
  },
  pageTitle: {
    fontWeight: 700,
    fontSize: '3rem',
    lineHeight: 1.2,
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
    [theme.breakpoints.down('sm')]: {
      fontSize: '2rem'
    }
  },
  pageSubtitle: {
    fontSize: '1.25rem',
    color: theme.palette.text.secondary,
    maxWidth: '700px',
    margin: '0 auto'
  },
  faqSection: {
    marginBottom: theme.spacing(6),
    maxWidth: '900px',
    margin: '0 auto',
    paddingBottom: theme.spacing(6)
  },
  question: {
    fontWeight: 600,
    fontSize: '1.5rem',
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.25rem'
    }
  },
  answer: {
    fontSize: '1.125rem',
    lineHeight: 1.8,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2)
  },
  listItem: {
    fontSize: '1.125rem',
    lineHeight: 1.8,
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(3),
    marginBottom: theme.spacing(1)
  },
  faqDivider: {
    marginTop: theme.spacing(6),
    marginBottom: theme.spacing(6)
  },
  ctaSection: {
    textAlign: 'center',
    marginTop: theme.spacing(10),
    paddingTop: theme.spacing(8),
    borderTop: `1px solid ${theme.palette.divider}`
  },
  ctaText: {
    fontSize: '1.125rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(3)
  },
  ctaButtons: {
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'center',
    flexWrap: 'wrap'
  },
  primaryBtn: {
    padding: theme.spacing(1.5, 4),
    fontSize: '1rem',
    fontWeight: 600,
    textTransform: 'none'
  },
  secondaryBtn: {
    padding: theme.spacing(1.5, 4),
    fontSize: '1rem',
    fontWeight: 600,
    textTransform: 'none',
    borderWidth: '2px',
    '&:hover': {
      borderWidth: '2px'
    }
  }
}));

const CompanyFAQ = () => {
  const classes = useStyles();

  usePageTitle('Company FAQ - Remote Job');

  return (
    <div className={classes.root}>
      <Helmet>
        <title>Company FAQ - Remote Hiring Platform | Remote Job</title>
        <meta
          name="description"
          content="Everything companies need to know about posting jobs, managing candidates, and pricing on Remote Job. No subscriptions, no recruiter fees, complete hiring tools included."
        />
        <meta
          name="keywords"
          content="remote hiring FAQ, job posting pricing, hiring platform questions, ATS pricing, remote recruitment, hiring tools"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Company FAQ - Remote Hiring Platform | Remote Job" />
        <meta
          property="og:description"
          content="Everything companies need to know about posting jobs, managing candidates, and pricing on Remote Job."
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Company FAQ - Remote Hiring Platform | Remote Job" />
        <meta
          name="twitter:description"
          content="Everything companies need to know about posting jobs, managing candidates, and pricing on Remote Job."
        />
      </Helmet>

      <Container maxWidth="lg">
        {/* Intro Section */}
        <Box className={classes.introSection}>
          <Typography className={classes.pageTitle} variant="h1">
            Hiring on Remote Job
          </Typography>
          <Typography className={classes.pageSubtitle} variant="h5">
            Everything companies need to know about posting jobs, managing candidates, and hiring with confidence.
          </Typography>
        </Box>

        {/* FAQ Items */}
        <Box className={classes.faqSection}>
          <Typography className={classes.question}>
            How does pricing work?
          </Typography>
          <Typography className={classes.answer}>
            Each job post is a one-time payment. There are no subscriptions and no recurring fees. You only pay when you publish a job.
          </Typography>
        </Box>

        <Divider className={classes.faqDivider} />

        <Box className={classes.faqSection}>
          <Typography className={classes.question}>
            Is the price per job or per account?
          </Typography>
          <Typography className={classes.answer}>
            Plans are per job post.
          </Typography>
          <Typography className={classes.answer}>
            Hiring Profiles are account-wide and apply discounts to every job you post.
          </Typography>
        </Box>

        <Divider className={classes.faqDivider} />

        <Box className={classes.faqSection}>
          <Typography className={classes.question}>
            What do I get when I post a job?
          </Typography>
          <Typography className={classes.answer}>
            Every job post includes a complete hiring workspace at no extra cost:
          </Typography>
          <ul style={{ margin: 0, paddingLeft: 0 }}>
            <li className={classes.listItem}>Drag-and-drop candidate pipeline</li>
            <li className={classes.listItem}>Application tracking</li>
            <li className={classes.listItem}>Interview scheduling</li>
            <li className={classes.listItem}>Direct messaging with candidates</li>
          </ul>
          <Typography className={classes.answer} style={{ marginTop: '16px' }}>
            Each job post gets its own dedicated workspace. No setup is required. Everything is ready as soon as your job is published.
          </Typography>
        </Box>

        <Divider className={classes.faqDivider} />

        <Box className={classes.faqSection}>
          <Typography className={classes.question}>
            Do I need to pay extra for candidate management or interviews?
          </Typography>
          <Typography className={classes.answer}>
            No. Candidate management, messaging, and interview scheduling are included by default in every plan.
          </Typography>
        </Box>

        <Divider className={classes.faqDivider} />

        <Box className={classes.faqSection}>
          <Typography className={classes.question}>
            What is a Hiring Profile?
          </Typography>
          <Typography className={classes.answer}>
            A Hiring Profile unlocks automatic discounts and access across all your job posts. You set it once at the account level, and every job post you publish benefits from it.
          </Typography>
        </Box>

        <Divider className={classes.faqDivider} />

        <Box className={classes.faqSection}>
          <Typography className={classes.question}>
            How do Hiring Profile discounts work?
          </Typography>
          <Typography className={classes.answer}>
            Depending on your profile:
          </Typography>
          <ul style={{ margin: 0, paddingLeft: 0 }}>
            <li className={classes.listItem}>Starter Profile saves up to 20% per job post</li>
            <li className={classes.listItem}>Professional Profile saves 35% per job post</li>
            <li className={classes.listItem}>Premium Profile saves 50% per job post</li>
          </ul>
          <Typography className={classes.answer} style={{ marginTop: '16px' }}>
            Discounts are applied automatically at checkout.
          </Typography>
        </Box>

        <Divider className={classes.faqDivider} />

        <Box className={classes.faqSection}>
          <Typography className={classes.question}>
            Can I change my Hiring Profile later?
          </Typography>
          <Typography className={classes.answer}>
            Yes. You can upgrade or change your profile at any time. There are no long-term commitments or contracts.
          </Typography>
        </Box>

        <Divider className={classes.faqDivider} />

        <Box className={classes.faqSection}>
          <Typography className={classes.question}>
            How long does my job post stay live?
          </Typography>
          <Typography className={classes.answer}>
            Your job post stays active until you choose to turn it off. You have full control over visibility at all times. Featured placements run for the duration included in your selected plan.
          </Typography>
        </Box>

        <Divider className={classes.faqDivider} />

        <Box className={classes.faqSection}>
          <Typography className={classes.question}>
            What does featured placement mean?
          </Typography>
          <Typography className={classes.answer}>
            Featured placement keeps your job post at the top of the job board for increased visibility. Higher plans include longer featured placement periods and branding options.
          </Typography>
        </Box>

        <Divider className={classes.faqDivider} />

        <Box className={classes.faqSection}>
          <Typography className={classes.question}>
            Can I target candidates by location?
          </Typography>
          <Typography className={classes.answer}>
            Yes. You can restrict job post visibility by geographic region to focus on candidates in your preferred markets.
          </Typography>
        </Box>

        <Divider className={classes.faqDivider} />

        <Box className={classes.faqSection}>
          <Typography className={classes.question}>
            How does messaging work?
          </Typography>
          <Typography className={classes.answer}>
            You can message candidates directly inside the platform. Conversations stay organized by candidate and job post, and your hiring team can collaborate in one place.
          </Typography>
        </Box>

        <Divider className={classes.faqDivider} />

        <Box className={classes.faqSection}>
          <Typography className={classes.question}>
            Is this better than using recruiters?
          </Typography>
          <Typography className={classes.answer}>
            Remote Job removes recruiter fees and long contracts. You post once, manage candidates directly, and hire faster without middlemen.
          </Typography>
        </Box>

        <Divider className={classes.faqDivider} />

        <Box className={classes.faqSection}>
          <Typography className={classes.question}>
            Is payment secure?
          </Typography>
          <Typography className={classes.answer}>
            Yes. Payments are processed securely, and your job post is published immediately after checkout.
          </Typography>
        </Box>

        <Divider className={classes.faqDivider} />

        <Box className={classes.faqSection}>
          <Typography className={classes.question}>
            What happens after I publish a job?
          </Typography>
          <Typography className={classes.answer}>
            You will start receiving applications directly into your hiring workspace where you can review candidates, message them, schedule interviews, and move them through your pipeline.
          </Typography>
        </Box>

        {/* CTA Section */}
        <Box className={classes.ctaSection}>
          <Typography className={classes.ctaText}>
            Ready to post a job? View pricing or publish your first role in minutes.
          </Typography>
          <Box className={classes.ctaButtons}>
            <Button
              component={RouterLink}
              to={APP_PATH_PRICING}
              variant="outlined"
              className={classes.secondaryBtn}>
              View Pricing
            </Button>
            <Button
              component={RouterLink}
              to={APP_PATH_SIGNUP}
              variant="contained"
              color="primary"
              className={classes.primaryBtn}>
              Post a Job
            </Button>
          </Box>
        </Box>
      </Container>
    </div>
  );
};

export default CompanyFAQ;
